import { deprecatedFlagFilters } from '@gonfalon/dogfood-flags/dogfood-flags';

import { type UIFlagFilter } from './types';

/**
 * Single source of truth for deprecated filter keys.
 * These filters are being phased out as of v4 of the API.
 */
export const deprecatedFilterKeys = [
  'hasDataExport',
  'targeting',
  'contextKindsEvaluated',
  'contextKindTargeted',
  'followerId',
  'status',
  'hasExperiment',
  'codeReferences',
] as const;

export type DeprecatedFilterKey = (typeof deprecatedFilterKeys)[number];

/**
 * Check if a specific deprecated filter is currently enabled.
 * Returns true if the filter should be included, false if it should be omitted.
 */
export function isDeprecatedFilterEnabled(key: DeprecatedFilterKey): boolean {
  return deprecatedFlagFilters()[key];
}

/**
 * Remove disabled deprecated filters from a filter object.
 * Use this when serializing filters for API or browser.
 */
export function omitDisabledFilters<T extends Partial<UIFlagFilter>>(filter: T): T {
  const disabled = deprecatedFilterKeys.filter((key) => !isDeprecatedFilterEnabled(key));
  const result = { ...filter };
  for (const key of disabled) {
    delete result[key];
  }
  return result;
}

/**
 * Get cleared values for enabled deprecated filters.
 * Use this when clearing all filters to only include deprecated filters that are still enabled.
 * @param emptySetFactory - Factory function to create empty set values (avoids coupling to Immutable.js)
 */
export function getClearedDeprecatedFilters<TSet>(emptySetFactory: () => TSet) {
  const deprecated = deprecatedFlagFilters();
  return {
    ...(deprecated.hasDataExport ? { hasDataExport: undefined } : {}),
    ...(deprecated.targeting ? { targeting: '' } : {}),
    ...(deprecated.contextKindTargeted ? { contextKindTargeted: '' } : {}),
    ...(deprecated.contextKindsEvaluated ? { contextKindsEvaluated: emptySetFactory() } : {}),
    ...(deprecated.followerId ? { followerId: '' } : {}),
    ...(deprecated.status ? { status: '' } : {}),
    ...(deprecated.hasExperiment ? { hasExperiment: undefined } : {}),
    ...(deprecated.codeReferences ? { codeReferences: undefined } : {}),
  };
}

/**
 * Mock all deprecated filters as enabled.
 * Call this in beforeEach to ensure tests run with deprecated filters included.
 */
export function mockAllDeprecatedFiltersEnabled() {
  vi.mocked(deprecatedFlagFilters).mockReturnValue({
    hasDataExport: true,
    targeting: true,
    contextKindsEvaluated: true,
    contextKindTargeted: true,
    followerId: true,
    status: true,
    hasExperiment: true,
    codeReferences: true,
  });
}

/**
 * Mock all deprecated filters as disabled.
 * Call this in beforeEach to test behavior when deprecated filters are removed.
 */
export function mockAllDeprecatedFiltersDisabled() {
  vi.mocked(deprecatedFlagFilters).mockReturnValue({
    hasDataExport: false,
    targeting: false,
    contextKindsEvaluated: false,
    contextKindTargeted: false,
    followerId: false,
    status: false,
    hasExperiment: false,
    codeReferences: false,
  });
}
